import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import useNotification from "../../components/notification/useNotification";
import {
  addUserGuide,
  deleteUserGuide,
  editUserGuide,
  getUserGuides,
} from "./userGuideSlice";
import {
  IAddUserGuidePayload,
  IEditUserGuidePayload,
  IUserGuideState,
} from "./userGuideTypes";
import UserGuideCards from "./components/UserGuideCards";
import SuperAdminUserGuide from "./components/SuperAdminUserGuide";
import { usePermissions } from "../../customHooks/usePermissions";
import { Status } from "../../common/constants";
import { useSpinner } from "../../customHooks";
import "./userGuide.scss";

const UserGuide = () => {
  const dispatch = useDispatch<any>();

  const { userGuides, isLoading, isSubmitting, isDeleting }: IUserGuideState =
    useSelector((state: any) => state.userGuide);

  const userGuideApiStatus =
    isLoading || isSubmitting || isDeleting ? Status.PENDING : Status.IDLE;

  useSpinner(userGuideApiStatus);

  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  const { hasPermissions } = usePermissions();
  const isSuperAdmin: boolean = hasPermissions("user.superadmin");

  React.useEffect(() => {
    dispatch(getUserGuides());
  }, [dispatch]);

  const refreshUserGuides = async (): Promise<void> => {
    await dispatch(getUserGuides());
  };

  const handleAddGuide = async (
    payload: IAddUserGuidePayload,
  ): Promise<void> => {
    try {
      const message = await dispatch(addUserGuide(payload)).unwrap();
      showSuccessNotification(message);
      await refreshUserGuides();
    } catch (error: any) {
      showWarningNotification(error || "Unable to add help guide.");
      throw error;
    }
  };

  const handleEditGuide = async (
    payload: IEditUserGuidePayload,
  ): Promise<void> => {
    try {
      const message = await dispatch(editUserGuide(payload)).unwrap();
      showSuccessNotification(message);
      await refreshUserGuides();
    } catch (error: any) {
      showWarningNotification(error || "Unable to update help guide.");
      throw error;
    }
  };

  const handleDeleteGuide = async (guideId: string): Promise<void> => {
    try {
      const message = await dispatch(deleteUserGuide(guideId)).unwrap();
      showSuccessNotification(message);
      await refreshUserGuides();
    } catch (error: any) {
      showWarningNotification(error || "Unable to delete help guide.");
      throw error;
    }
  };

  return (
    <div className="user-guide-page">
      {isSuperAdmin ? (
        <SuperAdminUserGuide
          userGuides={userGuides}
          isSubmitting={isSubmitting}
          isDeleting={isDeleting}
          onAddGuide={handleAddGuide}
          onEditGuide={handleEditGuide}
          onDeleteGuide={handleDeleteGuide}
        />
      ) : (
        <>
          <div className="user-guide-heading">
            <h1>User Guide</h1>
          </div>

          <div className="user-guide-body">
            <UserGuideCards userGuides={userGuides} />
          </div>
        </>
      )}
    </div>
  );
};

export default UserGuide;
